import React, { useEffect, useState } from 'react';
import { Modal, Select, message } from 'antd';
import Rest from 'tools/rest';
import IntegerQtyInput from './integerQtyInput';
import {
  formatQuantityWithUnit,
  materialDisplayUnit,
  parseMaterialIntegerQty,
  availableQtyFloor,
  isQuantityExplicitZero,
} from '../utils';

const ReturnToWarehouseModal = ({ visible, item, currentUser, onClose, onSuccess }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoWarehouse, setAutoWarehouse] = useState(null);
  const [allowChooseWarehouse, setAllowChooseWarehouse] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setWarehouseId(null);
    setQuantity(null);
    setAutoWarehouse(null);
    setAllowChooseWarehouse(false);

    if (item?.id) {
      Rest.get('/api/v1/material_moves.json', {
        params: {
          per: 1,
          page: 1,
          order: 'desc',
          order_by: 'created_at',
          search: {
            material_id: item.material_id,
            destination_type: 'Inventory',
            destination_id: item.id,
            source_type: 'WarehouseMaterial',
            approved: 'approved',
          },
        },
      })
        .then((r) => {
          const move = (r.data.material_moves || [])[0];
          const wmId = move?.source_id;
          if (!wmId) return;

          setAutoWarehouse({ warehouse_material_id: wmId, label: move.source_label || 'Склад' });
          setWarehouseId(wmId);
        })
        .catch(() => {});
    }

    Rest.get('/api/v1/warehouses.json')
      .then((r) => setWarehouses(r.data.warehouses || []))
      .catch(() => setWarehouses([]));
  }, [visible, item?.id]);

  const handleOk = async () => {
    if (!warehouseId) {
      message.error('Не удалось определить склад. Выберите вручную.');
      setAllowChooseWarehouse(true);
      return;
    }
    if (isQuantityExplicitZero(quantity)) {
      message.error('Количество должно быть больше 0');
      return;
    }
    const q = parseMaterialIntegerQty(quantity);
    if (q === null) {
      message.error('Укажите целое количество не меньше 1');
      return;
    }
    const available = availableQtyFloor(item);
    if (q > available) {
      message.error(
        `Недостаточно (доступно: ${formatQuantityWithUnit(available, materialDisplayUnit(item))})`
      );
      return;
    }

    setLoading(true);
    try {
      let destinationWarehouseMaterialId = null;
      if (autoWarehouse?.warehouse_material_id && warehouseId === autoWarehouse.warehouse_material_id) {
        destinationWarehouseMaterialId = autoWarehouse.warehouse_material_id;
      } else {
        const ensured = await Rest.post('/api/v1/warehouse_materials/ensure.json', {
          warehouse_id: warehouseId,
          material_id: item.material_id,
        });
        const wm = ensured.data?.warehouse_material;
        if (!wm?.id) throw new Error('Не удалось подготовить позицию склада');
        destinationWarehouseMaterialId = wm.id;
      }

      await Rest.post('/api/v1/material_moves.json', {
        material_move: {
          source_type: 'Inventory',
          source_user_id: currentUser.id,
          material_id: item.material_id,
          destination_type: 'WarehouseMaterial',
          destination_id: destinationWarehouseMaterialId,
          quantity: q,
        },
      });
      message.success('Возврат создан и ожидает подтверждения');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      message.error(err.response?.data?.errors?.join(', ') || err.message || 'Ошибка возврата');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Вернуть на склад: ${item?.material_name || ''}`}
      open={visible}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={loading}
      okText="Создать возврат"
      cancelText="Отмена"
    >
      <p style={{ marginBottom: 8 }}>
        Склад:{' '}
        <strong>
          {autoWarehouse?.label
            ? autoWarehouse.label
            : (allowChooseWarehouse ? 'выберите вручную' : 'определяем...')}
        </strong>
      </p>

      {allowChooseWarehouse && (
        <Select
          value={warehouseId}
          onChange={(v) => setWarehouseId(v)}
          placeholder="Выберите склад"
          style={{ width: '100%', marginBottom: 16 }}
          allowClear
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
        />
      )}

      <p style={{ marginBottom: 4 }}>
        Доступно:{' '}
        <strong>{formatQuantityWithUnit(item?.quantity, materialDisplayUnit(item))}</strong>
      </p>
      <IntegerQtyInput
        value={quantity}
        onChange={setQuantity}
        style={{ width: '100%' }}
        placeholder="Только целое число"
      />
    </Modal>
  );
};

export default ReturnToWarehouseModal;

